// What an area's routes.tsx exports: a list of AreaRoute, pure data. The
// registry (registry.tsx) puts each route into its shell, loads its page
// lazily, and shows the placeholder for screens that are not built yet.
//
//   export const routes: AreaRoute[] = [
//     screen(2, page(() => import("./pages/LoginPage"), "LoginPage")),   // built page
//     screen(4),                                                           // placeholder
//     screen([63, 51, 66], page(() => import("./pages/ActivityRecordPage"), "ActivityRecordPage")), // one path, several screens
//     extra("/join/:token", page(() => import("./pages/AcceptInvitePage"), "AcceptInvitePage")),     // not a sitemap screen
//   ];
import type { ComponentType } from "react";
import { coverageRow, shellFor, type ShellKind } from "./coverage";

export type PageModule = { default: ComponentType };
export type PageLoader = () => Promise<PageModule>;
export type PlaceholderLink = { label: string; to: string };

export type AreaRoute = {
  /** Absolute path pattern, e.g. "/p/:projectId/experts/:id". */
  path: string;
  /** COVERAGE rows this route draws (several when screens share one path). */
  screens: number[];
  /** The page. Omitted: the "being built" placeholder. */
  load?: PageLoader;
  /** Default: from the path (coverage.ts shellFor). */
  shell?: ShellKind;
  /** Signed-in pages that work before the person has an organisation (/settings/account). */
  allowNoOrg?: boolean;
  /** Placeholder only: links to pages that already do part of the job (":projectId" is filled in). */
  links?: PlaceholderLink[];
  /** An old address: forwards here (":params" filled in, the query kept). No page then. */
  redirectTo?: string;
};

/**
 * A page's code file could not be loaded (LF-N1, retest 30 Oct): after a new
 * version is deployed, a browser tab opened before it still asks for the old
 * file names, which are gone. "Add a reading" then showed "Something went
 * wrong" on the first click, and a reload worked.
 */
export function isChunkLoadError(e: unknown): boolean {
  const message = e instanceof Error ? e.message : String(e);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|is not a valid JavaScript MIME type/i.test(message);
}

const RELOADED_AT = "convenor:reloaded-for-new-version";

/**
 * Reloads the page once to get the new version; false when it already did in
 * the last minute (then the error page shows, so a broken file never loops).
 */
export function reloadForNewVersion(reload: () => void = () => window.location.reload(), now: number = Date.now()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOADED_AT) ?? 0);
    if (now - last < 60_000) return false;
    window.sessionStorage.setItem(RELOADED_AT, String(now));
  } catch {
    return false; // no session storage: no guard against a loop, so no reload
  }
  reload();
  return true;
}

/** Loads a page's code; a file of an older version that is gone reloads the page once instead of failing. */
export function loadPage<K extends string>(importer: () => Promise<Record<K, ComponentType<any>>>, name: K, reload?: () => void): Promise<PageModule> {
  return importer().then(
    (m) => ({ default: m[name] as ComponentType }),
    (e: unknown) => {
      // The reload replaces this page: wait for it rather than flash the error page.
      if (isChunkLoadError(e) && reloadForNewVersion(reload)) return new Promise<PageModule>(() => {});
      throw e;
    },
  );
}

/** A named export as a lazy page: page(() => import("./pages/X"), "X"). */
export function page<K extends string>(importer: () => Promise<Record<K, ComponentType<any>>>, name: K): PageLoader {
  return () => loadPage(importer, name);
}

/** A sitemap screen (or several sharing one path) at its COVERAGE route. */
export function screen(n: number | number[], load?: PageLoader, opts: Partial<Omit<AreaRoute, "screens" | "load">> = {}): AreaRoute {
  const screens = Array.isArray(n) ? n : [n];
  const row = coverageRow(screens[0]);
  return { path: row.path, screens, load, shell: opts.shell ?? shellFor(row.path), ...opts };
}

/** A route that is not a sitemap screen (legacy pages, join links, print views). */
export function extra(path: string, load: PageLoader, opts: Partial<Omit<AreaRoute, "path" | "load">> = {}): AreaRoute {
  return { path, screens: [], load, shell: opts.shell ?? shellFor(path), ...opts };
}

/** An old address that forwards to its new place: moved("/org/goals", "/org/insight/goals"). */
export function moved(path: string, to: string, opts: Partial<Omit<AreaRoute, "path" | "load" | "redirectTo">> = {}): AreaRoute {
  return { path, screens: [], redirectTo: to, shell: opts.shell ?? shellFor(path), ...opts };
}
